import {
  keepPreviousData,
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { PageMeta, PageQuery, Paginated } from '@lumina/types';
import { useCallback, useMemo, useState } from 'react';
import { api, isApiError, type QueryParams } from './client';

/** Human-readable message for any thrown value. Never leaks stack traces. */
export function errorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (isApiError(error)) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

/**
 * Server data is cached briefly and refetched after mutations (invalidate by query key).
 * 4xx responses are not retried: they will not succeed on a second attempt.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => isApiError(error) && error.isRetryable && failureCount < 2,
      },
      mutations: { retry: false },
    },
  });
}

// ---------------------------------------------------------------------------------------------
// Query-key factories
// ---------------------------------------------------------------------------------------------

type Id = string | number;

/**
 * Consistent keys per resource so a mutation can invalidate precisely:
 *   const patronKeys = createQueryKeys('patrons');
 *   patronKeys.list({ page: 2 })   -> ['patrons', 'list', { page: 2 }]
 *   patronKeys.detail(7)           -> ['patrons', 'detail', 7]
 *   queryClient.invalidateQueries({ queryKey: patronKeys.all })   // everything for patrons
 */
export function createQueryKeys<Scope extends string>(scope: Scope) {
  return {
    all: [scope] as const,
    lists: () => [scope, 'list'] as const,
    list: (params?: object) => [scope, 'list', params ?? {}] as const,
    details: () => [scope, 'detail'] as const,
    detail: (id: Id, extra?: string) => (extra ? ([scope, 'detail', id, extra] as const) : ([scope, 'detail', id] as const)),
    custom: (...parts: (string | number | object)[]) => [scope, ...parts] as const,
  };
}

// ---------------------------------------------------------------------------------------------
// Query / mutation wrappers
// ---------------------------------------------------------------------------------------------

type QueryOpts<T> = Omit<UseQueryOptions<T, Error, T, QueryKey>, 'queryKey' | 'queryFn'>;

/**
 * GET `path` with typed result and automatic abort.
 *   const { data } = useApiQuery<Biblio>(biblioKeys.detail(id), `/catalog/biblios/${id}`);
 */
export function useApiQuery<T>(key: QueryKey, path: string, query?: QueryParams, options?: QueryOpts<T>): UseQueryResult<T, Error> {
  return useQuery<T, Error, T, QueryKey>({
    queryKey: key,
    queryFn: ({ signal }) => api.get<T>(path, { query, signal }),
    ...options,
  });
}

/**
 * Paginated list. Keeps the previous page on screen while the next loads (no flicker).
 *   const { data } = useApiPaginatedQuery<PatronRow>(patronKeys.list(q), '/patrons', q);
 */
export function useApiPaginatedQuery<T>(
  key: QueryKey,
  path: string,
  query: PageQuery & QueryParams,
  options?: QueryOpts<Paginated<T>>,
): UseQueryResult<Paginated<T>, Error> {
  return useQuery<Paginated<T>, Error, Paginated<T>, QueryKey>({
    queryKey: key,
    queryFn: ({ signal }) => api.get<Paginated<T>>(path, { query, signal }),
    placeholderData: keepPreviousData,
    ...options,
  });
}

type MutationFn<TData, TVars> = (vars: TVars, signal?: AbortSignal) => Promise<TData>;

interface ApiMutationOptions<TData, TVars> extends Omit<UseMutationOptions<TData, Error, TVars>, 'mutationFn'> {
  /** Query keys invalidated after success (prefix match). */
  invalidate?: readonly QueryKey[];
}

/**
 * Mutation with automatic cache invalidation.
 *   const checkout = useApiMutation((vars: CheckoutRequest) => api.post<CheckoutResult>('/circulation/checkout', vars),
 *     { invalidate: [circulationKeys.all] });
 */
export function useApiMutation<TData, TVars = void>(
  fn: MutationFn<TData, TVars>,
  options: ApiMutationOptions<TData, TVars> = {},
): UseMutationResult<TData, Error, TVars> {
  const queryClient = useQueryClient();
  const { invalidate, onSuccess, ...rest } = options;
  return useMutation<TData, Error, TVars>({
    ...rest,
    mutationFn: (vars) => fn(vars),
    onSuccess: async (data, vars, context, meta) => {
      if (invalidate?.length) {
        await Promise.all(invalidate.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
      }
      return onSuccess?.(data, vars, context, meta);
    },
  });
}

// ---------------------------------------------------------------------------------------------
// Pagination helpers (match Paginated<T> / PageMeta in @lumina/types)
// ---------------------------------------------------------------------------------------------

export const PAGE_SIZES = [10, 25, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 25;

export function emptyPage<T>(pageSize = DEFAULT_PAGE_SIZE): Paginated<T> {
  return { data: [], meta: { page: 1, pageSize, total: 0, totalPages: 0 } };
}

/** 1-based inclusive range shown on the current page: { from: 26, to: 50 }. */
export function pageRange(meta: PageMeta): { from: number; to: number } {
  if (meta.total === 0) {
    return { from: 0, to: 0 };
  }
  const from = (meta.page - 1) * meta.pageSize + 1;
  return { from, to: Math.min(meta.total, from + meta.pageSize - 1) };
}

/** Compact page list with ellipses: [1, '...', 4, 5, 6, '...', 20]. */
export function pageWindow(page: number, totalPages: number, siblings = 1): (number | 'gap')[] {
  if (totalPages <= 1) {
    return totalPages === 1 ? [1] : [];
  }
  const items = new Set<number>([1, totalPages]);
  for (let p = page - siblings; p <= page + siblings; p += 1) {
    if (p >= 1 && p <= totalPages) {
      items.add(p);
    }
  }
  const sorted = [...items].sort((a, b) => a - b);
  const out: (number | 'gap')[] = [];
  sorted.forEach((p, i) => {
    const prev = sorted[i - 1];
    if (i > 0 && prev !== undefined && p - prev > 1) {
      out.push('gap');
    }
    out.push(p);
  });
  return out;
}

/** Sort state <-> the API's "field" / "-field" string. */
export interface SortState {
  field: string;
  direction: 'asc' | 'desc';
}

export function toSortParam(sort: SortState | null | undefined): string | undefined {
  return sort ? `${sort.direction === 'desc' ? '-' : ''}${sort.field}` : undefined;
}

export function fromSortParam(param: string | null | undefined): SortState | null {
  if (!param) {
    return null;
  }
  return param.startsWith('-') ? { field: param.slice(1), direction: 'desc' } : { field: param, direction: 'asc' };
}

export interface PaginationState {
  page: number;
  pageSize: number;
  sort: SortState | null;
  q: string;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setSort: (sort: SortState | null) => void;
  setQ: (q: string) => void;
  reset: () => void;
  /** Ready-made query params for useApiPaginatedQuery. */
  query: PageQuery & QueryParams;
}

/**
 * Local state for a server-paginated list. Changing page size, sort or search returns to page 1.
 *   const pg = usePagination();  const { data } = useApiPaginatedQuery(keys.list(pg.query), '/things', pg.query);
 */
export function usePagination(initial: { pageSize?: number; sort?: SortState | null } = {}): PaginationState {
  const [page, setPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState<number>(initial.pageSize ?? DEFAULT_PAGE_SIZE);
  const [sort, setSortState] = useState<SortState | null>(initial.sort ?? null);
  const [q, setQState] = useState('');

  const setPage = useCallback((next: number) => setPageState(Math.max(1, next)), []);
  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
    setPageState(1);
  }, []);
  const setSort = useCallback((next: SortState | null) => {
    setSortState(next);
    setPageState(1);
  }, []);
  const setQ = useCallback((next: string) => {
    setQState(next);
    setPageState(1);
  }, []);
  const reset = useCallback(() => {
    setPageState(1);
    setQState('');
    setSortState(initial.sort ?? null);
  }, [initial.sort]);

  const query = useMemo<PageQuery & QueryParams>(() => ({ page, pageSize, q: q || undefined, sort: toSortParam(sort) }), [page, pageSize, q, sort]);
  return { page, pageSize, sort, q, setPage, setPageSize, setSort, setQ, reset, query };
}
